import { useEffect, useState } from "react";
import { api, fmtAUD, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Target, Trophy } from "lucide-react";

export default function MyGoalsCard() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    api.get("/goals/me")
      .then((r) => { if (!cancelled) setGoals(r.data); })
      .catch(() => { if (!cancelled) setGoals([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const today = new Date().toISOString().slice(0, 10);
  const active = goals.filter((g) => g.period_start <= today && g.period_end >= today);
  const upcoming = goals.filter((g) => g.period_start > today);
  const past = goals.filter((g) => g.period_end < today);
  const display = active.length ? active : upcoming.length ? upcoming.slice(0, 1) : past.slice(0, 1);

  if (loading) {
    return (
      <div className="eyb-card p-5" data-testid="my-goals-card">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-[#29ABE2]" />
          <h3 className="font-heading font-semibold text-slate-900">My Goals</h3>
        </div>
        <div className="text-xs text-slate-400">Loading…</div>
      </div>
    );
  }

  if (goals.length === 0) {
    return (
      <div className="eyb-card p-5" data-testid="my-goals-card">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-[#29ABE2]" />
          <h3 className="font-heading font-semibold text-slate-900">My Goals</h3>
        </div>
        <p className="text-xs text-slate-500">
          No revenue target has been set yet. Your administrator will assign goals based on closed-won opportunities.
        </p>
      </div>
    );
  }

  return (
    <div className="eyb-card p-5" data-testid="my-goals-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[#29ABE2]" />
          <h3 className="font-heading font-semibold text-slate-900">My Goals</h3>
        </div>
        <span className="text-[11px] text-slate-400">{active.length ? "Active period" : upcoming.length ? "Upcoming" : "Most recent"}</span>
      </div>
      <div className="space-y-5">
        {display.map((g) => {
          const pct = Math.min(100, Number(g.percent) || 0);
          const hit = Number(g.percent) >= 100;
          return (
            <div key={g.id} data-testid={`my-goal-${g.id}`}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">
                    {g.label || `Goal · ${fmtDate(g.period_start)} → ${fmtDate(g.period_end)}`}
                  </div>
                  {g.label && (
                    <div className="text-[11px] text-slate-500">{fmtDate(g.period_start)} → {fmtDate(g.period_end)}</div>
                  )}
                </div>
                {hit && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    <Trophy className="w-3 h-3" /> Hit
                  </span>
                )}
              </div>
              <div className="mt-3">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-bold text-slate-900">{fmtAUD(g.achieved_amount)}</span>
                  <span className="text-slate-500">of {fmtAUD(g.target_amount)}</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full transition-all ${hit ? "bg-emerald-500" : "bg-gradient-to-r from-[#29ABE2] to-[#1B4F8B]"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500">
                  <span>{g.won_count} closed-won deal{g.won_count === 1 ? "" : "s"}</span>
                  <span className={`font-semibold ${hit ? "text-emerald-600" : "text-slate-700"}`}>{Number(g.percent).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {goals.length > display.length && (
        <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
          {goals.length - display.length} other goal{goals.length - display.length === 1 ? "" : "s"} on record
        </div>
      )}
    </div>
  );
}
